// import '/assets/styles/sections/header/menu-desktop.scss';
// import '/assets/styles/sections/header/menu-desktop-submenu.scss';
import config from '/config';

import { useRef, useState, useEffect } from 'react';

// Functions
import isExact from '@wearetla/tla-essentials-tools/functions/is-exact';

// Partials
import Btn from '/views/partials/btn';
import { Input } from '/views/partials/forms'
import RoundBtn from '/views/partials/round-btn';
import Simplebar from '/views/partials/simplebar';
import Icon from '@wearetla/tla-essentials-tools/partials/icon';

// Sections
import ChildForm from '/views/sections/child-form'

// Hooks
import useVisible from '@wearetla/tla-essentials-tools/hooks/visible'
import useDebounce from '@wearetla/tla-essentials-tools/hooks/debounce'

// Data
import ageGroups from '/data/age-groups'

// Services
import userServices from '/services/user'

// Context
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';
import { useBreakpoints } from '@wearetla/tla-essentials-tools/utilities/breakpoints';
import { useChildren } from '/utilities/children';
import { useAuth } from '@wearetla/tla-essentials-tools/utilities/auth';
import useHeader from '../hooks/use-header';

const ChildMenu = () => {
	const { showChildMenu: show, setShowChildMenu } = useHeader();
	const { childData, childSelection, setChildSelection, getChildren } = useChildren();
	const { mobile } = useBreakpoints();
	const { userData } = useAuth();
	const { openModal } = useModals();

	const container = useRef();
	const syncedSelection = useRef(childSelection);
	
	const _mounted = useRef(false);
	const [inDom, visible] = useVisible(show);

	const [selections, setSelections] = useState(childSelection);
	const [addMode, setAddMode] = useState(false);
	const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);

	const debouncedSelections = useDebounce(selections, 250);

	const close = () => {
		setShowChildMenu(false);
	}

	// Functs
	const toggleAddMode = () => {
		if(userData) {
			setAddMode(!addMode);
		}
		else {
			openModal('login-message', {
				message: 'Bebek eklemek için hesabınıza giriş yapmalısınız.',
				redirect: 'account.children',
			})
		}
	}

	const bodyClickEvent = useRef((e) => {
		if(container.current && !container.current.contains(e.target)) {
			close();
		}
	})

	const selectChild = (child) => {
		setAddMode(false);
		setSelectedAgeGroup(null);
		setSelections({
			...selections,
			empty: false,
			age: child.ageGroup.value,
			gender: child.gender,
			id: child.id,
		});
	}

	const selectGender = (gender) => {
		setSelectedAgeGroup(null);
		setAddMode(false);
		setSelections({
			...selections,
			empty: false,
			id: false,
			gender: gender,
			age: false,
		})
	}

	const selectAgeGroup = (group) => {
			setSelectedAgeGroup(group);
			if(!group || group.value !== selections.age) {
				setSelections({
					...selections,
					empty: false,
					age: group ? group.value : false,
				})
			}
	}

	// Effects
	useEffect(() => {
		_mounted.current = true;

		return () => {
			_mounted.current = false;
		}
	}, [])

	useEffect(() => {
		if(_mounted.current && !isExact(childSelection, debouncedSelections)){
			if(isExact(syncedSelection.current, debouncedSelections)) {
				setSelections(childSelection);
				syncedSelection.current = childSelection;
			}
			else {
				setChildSelection(debouncedSelections);
				syncedSelection.current =debouncedSelections;
			}
		}
	}, [debouncedSelections, childSelection])

	useEffect(() => {
		const foundGroup = ageGroups.find(group => group.value === childSelection.age);
		setSelectedAgeGroup(foundGroup ? foundGroup : null);
	}, [childSelection]);

	useEffect(() => {
		if(inDom) {
			window.document.body.addEventListener('click', bodyClickEvent.current);
		}
		else {
			if(_mounted.current) {
				setAddMode(false);
			}
			window.document.body.removeEventListener('click', bodyClickEvent.current);
		}
	}, [inDom])
	
	useEffect(() => {
		if(inDom && mobile) {
			document.body.classList.add("block-overflow-childmenu");
		}
		else {
			document.body.classList.remove("block-overflow-childmenu");
		}
	}, [inDom, mobile])

	if(inDom) {
		return (
			<aside className={'section header-childmenu' + (visible ? ' show' :'')} ref={container}>
				<div className="childmenu-outerwrap">
					<div className="childmenu-wrap wrapper narrow">
						<div className="childmenu-selection">
							<strong className="selection-title">
								Bebeğinizin bilgilerini girin <br />ya da cinsiyet seçin

								{mobile &&
									<RoundBtn onClick={close} className="title-closebtn" />
								}
							</strong>

							<Simplebar
								className="selection-opts">
								<div className="opts-wrap">
									<button
										className={'opts-item' + (addMode ? ' active' : '')}
										onClick={toggleAddMode}>
										<Icon className="item-icon small" name="plus" />
										<span className="item-name">Ekle</span>
									</button>
									{childData.map((child, nth) => {
										const childGender = child.gender === 1 ? 'boy' : 'girl';
										return (
											<button
												key={nth}
												className={'opts-item ' + childGender + (!addMode && selections.id === child.id ? ' active' : '')}
												onClick={() => { selectChild(child); }}>
												<Icon className="item-icon" name={childGender} />
												<span className="item-name">{child.name}</span>
											</button>
										)
									})}
									<button
										onClick={() => { selectGender(false); }}
										className={'opts-item all' + (!addMode && !selections.id && selections.gender === false ? ' active' : '')}>
										<Icon className="item-icon" name="gender" />
										<span className="item-name">Tüm</span>
									</button>
									<button
										onClick={() => { selectGender(1); }}
										className={'opts-item boy' + (!addMode && !selections.id && selections.gender === 1 ? ' active' : '')}>
										<Icon className="item-icon" name="boy" />
										<span className="item-name">Erkek</span>
									</button>
									<button
										onClick={() => { selectGender(2); }}
										className={'opts-item girl' + (!addMode && !selections.id && selections.gender === 2 ? ' active' : '')}>
										<Icon className="item-icon" name="girl" />
										<span className="item-name">Kız</span>
									</button>
									<button
										onClick={() => { selectGender(config.listingPregnantGender); }}
										className={'opts-item pregnant' + (!addMode && !selections.id && selections.gender === config.listingPregnantGender ? ' active' : '')}>
										<Icon className="item-icon" name="pregnant" />
										<span className="item-name">Hamile</span>
									</button>
								</div>
							</Simplebar>
						</div>

						{addMode &&
							<div className="childmenu-bottom childmenu-add">
								<ChildForm
									className="add-form"
									onSuccess={() => {
										setAddMode(false);
										getChildren();
									}} />
							</div>
						}

						{(!addMode && !selections.id && selections.gender !== config.listingPregnantGender) &&
							<div className="childmenu-bottom childmenu-age">
								<strong className="age-title">
									Bebeğiniz kaç aylık?
								</strong>

								<Input
									inlineBlock
									className="age-input"
									name="age"
									type="radio"
									onChange={selectAgeGroup}
									value={selectedAgeGroup}
									options={ageGroups.map((group) => ({
										value: group.value,
										label: group.label,
									}))} />

								{(selectedAgeGroup && !mobile) &&
									<div className="age-controls">
										<button
											type="button"
											onClick={() => {
												selectAgeGroup(null);
											}}
											className="controls-clearbtn">
											seçimi temizle
										</button>
									</div>
								}
							</div>
						}
					</div>
				</div>
				<div className="childmenu-subcontrols">
					{((selectedAgeGroup || selections.gender) && mobile) &&
						<div className="subcontrols-minibtns">
							<button
								type="button"
								onClick={() => {
									selectAgeGroup(null);
									selectGender(false);
								}}
								className="minibtns-clearbtn">
								Seçimi iptal et
							</button>
						</div>
					}
					<div className="subcontrols-wrap">
						<Btn
							onClick={close}
							className={`subcontrols-btn primary${mobile ? ' block' : ' wide'}`}>
							Uygula
						</Btn>
					</div>
				</div>
			</aside>
		)
	}
	else {
		return false;
	}
}

export default ChildMenu;